import jobenquiryService from '../../../services/jobenquiryService';
import filterService from '../../../services/filterService';

import {
    setJobenquiryLoading,
    setJobenquiryError,
    setJobenquiryList,
    setJobenquiryCurrent,
    setJobenquiryPagination,
    setJobenquirySort,
    setJobenquiryFilters,
    setJobenquiryDefaultFilter,
    setJobenquirySettings,
    clearJobenquiryCurrent,
    clearJobenquiryError,
    setJobenquiryFilterLoading,
    changeJobenquiryFilterCondition,
    addJobenquiryFilterCondition,
    updateJobenquiryFilterConditionRule,
    updateJobenquiryFilterConditionValue,
    removeJobenquiryFilterCondition,
    setJobenquirySelected,
    setJobenquiryDownloading,
    resetJobenquiryFiltersAndPagination,

    setJobenquiryClassicFilters
} from './jobenquirySlice';

export const fetchSettings = () => async (dispatch, getState) => {
    try {
        // Only fetch settings if they haven't been initialized
        if (!getState().jobenquiry.isSettingsInitialized) {
            dispatch(setJobenquiryLoading(true));
            const response = await jobenquiryService.getSettings();

            // Set the settings
            dispatch(setJobenquirySettings(response));

            const classicFilters = response?.classic_filters || [];
            dispatch(setJobenquiryClassicFilters(classicFilters));

            const activeFilter = response.filters.find(filter => filter.mark_default === true)
            if (activeFilter) {
                dispatch(setJobenquiryDefaultFilter(activeFilter));
                return activeFilter;
            }
        }
        return {};
    } catch (error) {
        console.error('Error fetching settings:', error);
        dispatch(setJobenquiryError({
            message: error.message || 'Failed to fetch settings',
            errors: error.errors || {}
        }));
        return {};
    } finally {
        dispatch(setJobenquiryLoading(false));
    }
};

export const fetchJobenquiries = (params = {}, filter = {}, shouldUseFilter, externalCondition = {}) => async (dispatch, getState) => {
    try {
        // Check if settings are initialized

        const state = getState().jobenquiry;
        if (!state.isSettingsInitialized) {
            shouldUseFilter ? filter = await dispatch(fetchSettings()) : await dispatch(fetchSettings());
        }

        const finalQueryParams = {
            ...state.filters,
            page: state.pagination.current_page,
            per_page: state.pagination.per_page,
            sort_field: state.sort.field,
            sort_direction: state.sort.direction,
            ...params
        };

        const finalActiveFilter = shouldUseFilter ? (Object.keys(filter).length === 0 ? state.defaultFilter : { ...state.defaultFilter, ...filter }) : externalCondition;

        dispatch(setJobenquiryLoading(true));
        const response = await jobenquiryService.getList(finalQueryParams, finalActiveFilter, state.classicFilters);
        dispatch(setJobenquiryList(response));
        return response;
    } catch (error) {
        console.error('Error in fetchJobenquiries:', error);
        const serializedError = {
            message: error.message || 'An error occurred',
            errors: error.errors || {},
            status: error.status || 500
        };
        dispatch(setJobenquiryError(serializedError));
        throw serializedError;
    } finally {
        dispatch(setJobenquiryLoading(false));
    }
};

export const fetchJobenquiry = (id) => async (dispatch) => {
    try {
        dispatch(setJobenquiryLoading(true));
        const response = await jobenquiryService.getOne(id);
        dispatch(setJobenquiryCurrent(response.data));
    } catch (error) {
        dispatch(setJobenquiryError({
            message: error.message || 'An error occurred',
            errors: error.errors || {}
        }));
    } finally {
        dispatch(setJobenquiryLoading(false));
    }
};

export const createJobenquiry = (data) => async (dispatch) => {
    try {
        const response = await jobenquiryService.create(data);
        return response.data || response;
    } catch (error) {
        const serializedError = {
            message: error.message || 'An error occurred',
            errors: error.errors || {}
        };
        throw serializedError;
    }
};

export const updateJobenquiry = (id, data) => async (dispatch) => {
    try {
        const response = await jobenquiryService.update(id, data);
        return response.data || response;
    } catch (error) {
        const serializedError = {
            message: error.message || 'An error occurred',
            errors: error.errors || {}
        };
        throw serializedError;
    }
};

export const deleteJobenquiry = (id) => async (dispatch) => {
    try {
        dispatch(setJobenquiryLoading(true));
        const response = await jobenquiryService.delete(id);
        return response;
    } catch (error) {
        const serializedError = {
            message: error.message || 'An error occurred',
            errors: error.errors || {}
        };
        dispatch(setJobenquiryError(serializedError));
        throw serializedError;
    } finally {
        dispatch(setJobenquiryLoading(false));
    }
};

export const downloadJobenquiry = (type) => async (dispatch, getState) => {
    try {
        const state = getState().jobenquiry;

        const finalQueryParams = {
            ...state.filters,
            page: state.pagination.current_page,
            per_page: state.pagination.per_page,
            sort_field: state.sort.field,
            sort_direction: state.sort.direction,
            export_type: type
        };

        const filter = state.defaultFilter;
        dispatch(setJobenquiryDownloading(true));
        const response = await jobenquiryService.download(finalQueryParams, filter, state.classicFilters);
        dispatch(setJobenquiryDownloading(false));

        return response;
    } catch (error) {
        console.error('Error downloading jobenquiries:', error);
        const serializedError = {
            message: error.message || 'An error occurred',
            errors: error.errors || {},
            status: error.status || 500
        };
        dispatch(setJobenquiryError(serializedError));
        throw serializedError;
    } finally {
        dispatch(setJobenquiryDownloading(false));
    }
};

export const setPagination = (pagination) => (dispatch) => {
    dispatch(setJobenquiryPagination(pagination));
};

export const setSort = (sort) => (dispatch) => {
    dispatch(setJobenquirySort(sort));
};

export const searchJobenquiries = (query) => (dispatch, getState) => {
    const currentFilters = getState().jobenquiry.filters;
    dispatch(setJobenquiryFilters({ ...currentFilters, search: query }));
};

// New filter-related actions
export const setDefaultFilter = (data) => {
    return async (dispatch) => {
        try {
            await dispatch(setJobenquiryDefaultFilter(data));
        } catch (error) {
            console.error('Error setting default filter:', error);
        }
    };
};

export const changeCondition = (condition) => {
    return (dispatch) => {
        dispatch(changeJobenquiryFilterCondition(condition));
    };
};

export const addNewCondition = (rule) => {
    return (dispatch) => {
        dispatch(addJobenquiryFilterCondition(rule));
    };
};

export const updateOptionRule = (index, data) => {
    return (dispatch) => {
        dispatch(updateJobenquiryFilterConditionRule({ index, data }));
    };
};

export const updateOptionValue = (index, value) => {
    return (dispatch) => {
        dispatch(updateJobenquiryFilterConditionValue({ index, value }));
    };
};

export const removeCondition = (index) => {
    return (dispatch) => {
        dispatch(removeJobenquiryFilterCondition(index));
    };
};

export const saveFilterCondition = (filterData) => async (dispatch, getState) => {
    try {
        dispatch(setJobenquiryFilterLoading(true));
        const response = await filterService.createFilterCondition(filterData);
        if (!response?.errors) {
            const currentSettings = getState().jobenquiry.settings;
            dispatch(setJobenquirySettings({ ...currentSettings, filters: response.filters }));
        }
        dispatch(setJobenquiryFilterLoading(false));
        return response;
    } catch (error) {
        console.error('Error saving filter:', error);
        dispatch(setJobenquiryFilterLoading(false));
        dispatch(setJobenquiryError({
            message: error.message || 'Failed to save filter',
            errors: error.errors || {}
        }));
        throw error;
    }
};

export const setSelected = (selectedRows) => {
    return (dispatch) => {
        dispatch(setJobenquirySelected(selectedRows));
    };
};

export const clearCurrentJobenquiry = () => {
    return (dispatch) => {
        dispatch(clearJobenquiryCurrent());
    };
};

export const resetFiltersAndPagination = () => (dispatch) => {
    dispatch(resetJobenquiryFiltersAndPagination());
};

export const changeClassicFilter = (name, value) => (dispatch, getState) => {
    const currentClassicFilters = getState().jobenquiry.classicFilters;

    // Create a deep copy of the classic filters
    const updatedClassicFilters = { ...currentClassicFilters };
    
    // Update the nested value in the query object
    if (updatedClassicFilters[name]) {
        updatedClassicFilters[name] = {
            ...updatedClassicFilters[name],
            query: {
                ...updatedClassicFilters[name].query,
                ...(typeof value === 'object' && value !== null ? value : { value })
            }
        };
    }

    dispatch(setJobenquiryClassicFilters(updatedClassicFilters));
};